import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Layout from './components/Layout/Layout';
import Home from './pages/Home';
import Stories from './pages/Stories';
import StoryDetail from './pages/StoryDetail';
import Events from './pages/Events';
import EventDetail from './pages/EventDetail';
import CreateStory from './pages/CreateStory';
import EditStory from './pages/EditStory';
import MyStories from './pages/MyStories';
import Profile from './pages/Profile';

// Import cache utilities to make them globally available
import './utils/cacheUtils';
import AdminPanel from './pages/AdminPanel';
import AdminEvents from './pages/AdminEvents';
import AdminStories from './pages/AdminStories';
import AdminJudges from './pages/AdminJudges';
import JudgeEvents from './pages/JudgeEvents';
import JudgeEventDetail from './pages/JudgeEventDetail';
import EventResultsPage from './pages/EventResultsPage';
import EventResults from './pages/EventResults';
import ResultsListing from './pages/ResultsListing';
import FAQ from './pages/FAQ';
import Support from './pages/Support';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import AuthCallback from './pages/AuthCallback';
import VerificationSuccess from './pages/VerificationSuccess';
import VerificationFailed from './pages/VerificationFailed';
import ProtectedRoute from './components/ProtectedRoute';
import PendingApproval from './components/PendingApproval';

// Create theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#667eea',
    },
    secondary: {
      main: '#764ba2',
    },
    background: {
      default: '#f5f5f5',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
    },
    h2: {
      fontWeight: 600,
    },
    h3: {
      fontWeight: 600,
    },
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 500,
    },
    h6: {
      fontWeight: 500,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
  },
});

// Component to handle app content based on user approval status
const AppContent = () => {
  const { user, loading } = useAuth();

  // Show loading while checking auth state
  if (loading) {
    return null; // Layout will handle loading state
  }

  // If user is authenticated but pending approval, show pending screen
  if (user && user.isPending) {
    return <PendingApproval />;
  }

  // Normal app flow for approved users and anonymous users
  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/stories" element={<ProtectedRoute><Stories /></ProtectedRoute>} />
        <Route path="/story/:id" element={<ProtectedRoute><StoryDetail /></ProtectedRoute>} />
        <Route path="/events" element={<ProtectedRoute><Events /></ProtectedRoute>} />
        <Route path="/events/:id" element={<ProtectedRoute><EventDetail /></ProtectedRoute>} />
        <Route 
          path="/events/:eventId/results" 
          element={
            <ProtectedRoute>
              <EventResults />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/results" 
          element={
            <ProtectedRoute>
              <ResultsListing />
            </ProtectedRoute>
          } 
        />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/callback" element={<AuthCallback />} />
        <Route path="/verification-success" element={<VerificationSuccess />} />
        <Route path="/verification-failed" element={<VerificationFailed />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        
        {/* Protected Routes */}
        <Route
          path="/create"
          element={
            <ProtectedRoute>
              <CreateStory />
            </ProtectedRoute>
          }
        />
        <Route
          path="/edit/:id"
          element={
            <ProtectedRoute>
              <EditStory />
            </ProtectedRoute>
          }
        />
        <Route
          path="/my-stories"
          element={
            <ProtectedRoute>
              <MyStories />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          }
        />
        <Route path="/support" element={<Support />} />
        <Route
          path="/admin"
          element={
            <ProtectedRoute requireAdmin>
              <AdminPanel />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/events"
          element={
            <ProtectedRoute requireAdmin>
              <AdminEvents />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/stories"
          element={
            <ProtectedRoute requireAdmin>
              <AdminStories />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/judges"
          element={
            <ProtectedRoute requireAdmin>
              <AdminJudges />
            </ProtectedRoute>
          }
        />
        <Route
          path="/judge/events"
          element={
            <ProtectedRoute requireJudge>
              <JudgeEvents />
            </ProtectedRoute>
          }
        />
        <Route
          path="/judge/events/:eventId"
          element={
            <ProtectedRoute requireJudge>
              <JudgeEventDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/events/:eventId/results"
          element={
            <ProtectedRoute requireAdmin>
              <EventResultsPage />
            </ProtectedRoute>
          }
        />
      </Routes>
    </Layout>
  );
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <AppContent />
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
